import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useRef, useState } from "react";
import { FileSpreadsheet, Table2, Upload } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"../../../components/ui/select";
import { cn } from "../../../lib/utils";
import { ACCEPTED_TYPES, TABLE_OPTIONS } from "../constants/tableOptions";

function isAcceptedExcelFile(file) {
  if (!file) return false;
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  // Some browsers leave file.type empty for .xls/.xlsx — fall back to extension.
  const name = file.name?.toLowerCase() ?? "";
  return name.endsWith(".xlsx") || name.endsWith(".xls");
}

export function OrgTableUploadForm({ onSubmit }) {
  const [selectedName, setSelectedName] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const validate = () => {
    const newErrors = {};
    if (!selectedName) newErrors.name = "Please select a table name.";
    if (!file) newErrors.file = "Please upload an Excel file.";
    return newErrors;
  };

  const handleFile = (incoming) => {
    if (!incoming) return;
    if (!isAcceptedExcelFile(incoming)) {
      setErrors((prev) => ({
        ...prev,
        file: "Only .xlsx or .xls files are accepted."
      }));
      return;
    }
    setFile(incoming);
    setErrors((prev) => ({ ...prev, file: undefined }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit?.({ name: selectedName, file });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedName("");
    setFile(null);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearFile = (e) => {
    e.stopPropagation();
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="w-full max-w-[760px] bg-white rounded-2xl border border-white/60 overflow-hidden">
      <div className="px-6 sm:px-8 py-3.5 border-b border-[var(--color-text-border)]/70 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-[var(--color-accent)]/10 grid place-items-center shrink-0">
          <Table2 className="h-[18px] w-[18px] text-[var(--color-accent)]" />
        </div>
        <h2 className="text-[18px] font-semibold text-foreground"><Trans i18nKey="dateTime.updateTable" /></h2>
      </div>

      <div className="px-6 sm:px-8 py-5 space-y-6 bg-[#F0FCFF]/20">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-foreground">
            <Trans i18nKey="orgTables.tableName" /> <span className="text-[var(--color-accent)]">*</span>
          </label>
          <Select
            value={selectedName || undefined}
            onValueChange={(value) => {
              setSelectedName(value);
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            disabled={submitting}>
            
            <SelectTrigger
              className={cn(
                "h-10 bg-white",
                errors.name && "border-[var(--color-error)] focus:ring-[var(--color-error)]"
              )}>
              
              <SelectValue placeholder={i18n.t("orgTables.selectATable")} />
            </SelectTrigger>
            <SelectContent>
              {TABLE_OPTIONS.map((option) =>
              <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          {errors.name &&
          <p className="text-xs text-[var(--color-error)] font-medium">{errors.name}</p>
          }
        </div>

        <div className="h-px bg-[var(--color-text-border)]/70" />

        <div className="space-y-2">
          <label className="text-sm font-semibold text-foreground">
            <Trans i18nKey="formActions.uploadFile" /> <span className="text-[var(--color-accent)]">*</span>
          </label>
          <p className="text-sm text-[var(--color-text-secondary)]">
            <Trans i18nKey="formActions.uploadAnExcelFileXlsxOrXls" />
          </p>

          <div
            role="button"
            tabIndex={0}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={cn(
              "rounded-xl border-2 border-dashed px-6 py-10 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors",
              dragOver ?
              "border-[var(--color-accent)] bg-[var(--color-accent)]/5" :
              errors.file ?
              "border-[var(--color-error)] bg-[var(--color-workspace)]" :
              "border-[var(--color-text-border)] bg-[var(--color-workspace)] hover:border-[var(--color-accent)]/50"
            )}>
            
            {file ?
            <>
                <FileSpreadsheet className="h-8 w-8 text-[var(--color-accent)]" />
                <div className="text-center">
                  <p className="text-sm font-semibold text-foreground mb-1">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} <Trans i18nKey="uom.kb" />
                  </p>
                </div>
                <Button type="button" variant="outline" className="h-8 px-3 text-xs" onClick={clearFile}>
                  <Trans i18nKey="formActions.remove" />
                </Button>
              </> :

            <>
                <div className="h-[52px] w-[52px] rounded-xl bg-muted grid place-items-center">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-foreground mb-1">
                    <Trans i18nKey="orgTables.dragDropYourFileHere" />
                  </p>
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    <Trans i18nKey="orgTables.or" />{" "}
                    <span className="text-[var(--color-accent)] font-medium"><Trans i18nKey="formActions.browseToUpload" /></span>
                  </p>
                </div>
                <div className="flex gap-2">
                  {[".xlsx", ".xls"].map((ext) =>
                <span
                  key={ext}
                  className="px-2.5 py-1 rounded-md bg-muted text-xs font-medium text-[var(--color-text-secondary)]">
                  
                      {ext}
                    </span>
                )}
                </div>
              </>
            }
          </div>

          {errors.file &&
          <p className="text-xs text-[var(--color-error)] font-medium">{errors.file}</p>
          }

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])} />
          
        </div>
      </div>

      <div className="px-6 sm:px-8 py-4 border-t border-[var(--color-text-border)]/70 bg-[var(--color-workspace)] flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={handleReset}
          disabled={submitting}
          className="h-11 px-6 rounded-xl border-[var(--color-primary)]/30 text-[var(--color-primary)] font-semibold text-sm hover:bg-[var(--color-workspace)]/40 bg-white">
          <Trans i18nKey="formActions.cancel" />
        
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="h-11 px-6 rounded-xl bg-[var(--color-primary)] text-white font-semibold text-sm hover:bg-[var(--color-accent)] transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] flex items-center justify-center min-w-[120px]">
          
          {submitting ? "Uploading…" : "Upload"}
        </Button>
      </div>
    </div>);

}
